import { GaugeTone, HumidityCard, HumidityStatus, ThermalCard, ThermalStatus } from "entities/assets";

import { ThermalCardData } from "modules/assets/ThermalCard";
import { TagTone } from "modules/assets/Tag";

import { formatLastUpdate } from "./co2";

const TEMP_STATUS_META: Record<ThermalStatus, { tone: TagTone; label: string; gauge: GaugeTone | null }> = {
  good: { tone: "green", label: "Good", gauge: "green" },
  warning: { tone: "orange", label: "Warning", gauge: "orange" },
  above: { tone: "red", label: "Above range", gauge: "red" },
  below: { tone: "red", label: "Below range", gauge: "red" },
  no_data: { tone: "gray", label: "No data available", gauge: null },
};

const HUM_STATUS_META: Record<HumidityStatus, { tone: TagTone; label: string }> = {
  good: { tone: "green", label: "Good" },
  warning: { tone: "orange", label: "Warning" },
  above: { tone: "red", label: "Above range" },
  below: { tone: "red", label: "Below range" },
  alert: { tone: "red", label: "Alert" },
  no_data: { tone: "gray", label: "No data available" },
};

const buildGauge = (rangeLow: number, rangeHigh: number, value: number, tone: GaugeTone) => {
  const span = Math.max(rangeHigh - rangeLow, 1);
  const pad = span * 0.5;
  const min = Math.min(rangeLow - pad, value - pad * 0.2);
  const max = Math.max(rangeHigh + pad, value + pad * 0.2);
  return { min, max, rangeLow, rangeHigh, value, tone };
};

export const buildTemperatureCard = (base: ThermalCardData, card: ThermalCard): ThermalCardData => {
  const meta = TEMP_STATUS_META[card.status];
  const hasValue = card.real_time !== null;
  return {
    ...base,
    status: { tone: meta.tone, label: meta.label },
    realtimeValue: hasValue ? `${card.real_time}` : "No data",
    realtimeUnit: hasValue ? card.unit : "",
    gauge: hasValue && meta.gauge ? buildGauge(card.t_min, card.t_max, card.real_time as number, meta.gauge) : undefined,
    rangeCaption: `Recommended range: ${card.t_min} - ${card.t_max} ${card.unit}`,
    alert: undefined,
    footerNote: card.sensor_count > 1 ? "Click to see per-sensor breakdown" : undefined,
    updatedNote: formatLastUpdate(card.last_update),
    outdatedNote: card.data_may_be_outdated ? "Data may be outdated" : undefined,
  };
};

export const buildHumidityCard = (base: ThermalCardData, card: HumidityCard): ThermalCardData => {
  const meta = HUM_STATUS_META[card.status];
  const hasValue = card.real_time !== null;
  const block = card.ceiling;
  return {
    ...base,
    status: { tone: meta.tone, label: meta.label },
    realtimeValue: hasValue ? `${card.real_time}` : "No data",
    realtimeUnit: hasValue ? card.unit : "",
    gauge:
      hasValue && card.bar_tone
        ? buildGauge(card.h_min, card.h_max, card.real_time as number, card.bar_tone)
        : undefined,
    rangeCaption: `Recommended range: ${card.h_min} - ${card.h_max} ${card.unit}`,
    alert: block
      ? {
          tone: block.tone,
          title: "Temperature-adjusted humidity limit",
          value: `${block.humidity} ${card.unit}`,
          lines: [
            `At current ${block.temperature}°C → max allowed: ${block.ceiling}%`,
            `Interpolated from: ${block.ceiling_27}%RH at 27°C ↔ ${block.ceiling_19}%RH at 19°C`,
          ],
          emphasis: block.exceeds
            ? `Exceeds limit: max ${block.ceiling}% at ${block.temperature}°C — Compliance Alert`
            : undefined,
        }
      : undefined,
    footerNote: card.sensor_count > 1 ? "Click to see per-sensor breakdown" : undefined,
    updatedNote: formatLastUpdate(card.last_update),
    outdatedNote: card.data_may_be_outdated ? "Data may be outdated" : undefined,
  };
};
